import { useEffect, useState, ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { IPayNow, getPayNowDetailsAndPaymentOptions } from '@acorn-portal/shared/src/store/paynow.slice';
import {
	PayNowStatus,
	GetAgreementDetailsResponseModel,
	GetPaymentCollectionOptionsResponseModel,
} from '@acorn-portal/shared/src/services/api';

function getCompanyHomePage(): string | null {
	const company = import.meta.env.VITE_AUTH_REALM;

	switch (company) {
		case 'Acorn':
			return 'https://acorninsure.co.uk';
		case 'Motorcade':
			return 'https://motorcade.co.uk';
		case 'Carrot':
			return 'https://carrotinsurance.com';
		case 'Flag':
			return 'https://www.flaginsurance.co.uk';
		case 'MyPolicy':
			return 'https://mypolicy.co.uk';
		case 'StreetCover':
			return 'https://streetcover.co.uk';
	}

	return null;
}

const companyHomePage = getCompanyHomePage();

const HomepageLink = () => (
	<>
		{companyHomePage ? (
			<Typography
				component='a'
				href={companyHomePage ?? ''}
				target='_blank'
				rel='noreferrer'
				sx={{ color: 'primary.main', fontWeight: 500 }}
			>
				homepage
			</Typography>
		) : (
			<Typography component='span'>homepage</Typography>
		)}
	</>
);

const messages = {
	noToken: <>No payment token has been provided. This page is inaccessible without a valid payment token.</>,
	fetchFailed: (
		<>
			Unable to retrieve your overdue payment details, please try again later or visit our <HomepageLink /> to speak to
			one of our agents to assist in setting up a payment plan to pay your overdue direct debit.
		</>
	),
	scheduleExists: (
		<>You have already entered into a payment schedule to pay off this direct debit. This link is no longer valid.</>
	),
	linkExpired: (
		<>
			Payment agreement link has expired, please visit our <HomepageLink /> to speak to one of our agents on web chat to
			assist in setting up a payment plan to pay your overdue direct debit.
		</>
	),
	policyCancelled: (
		<>
			Your policy has been cancelled or expired please visit our <HomepageLink /> to speak to one of our agents for
			further assistance.
		</>
	),
	fetchOptionsFailed: (
		<>
			Failed to retrieve payment plan options for your overdue direct debit, please visit our <HomepageLink /> to speak
			to one of our agents using our web chat option to assist in setting up a payment plan to pay your overdue direct
			debit.
		</>
	),
};

export enum PayNowErrorSeverity {
	INFO = 'info',
	WARNING = 'warning',
	ERROR = 'error',
}

interface UsePayNowDetailsTokenReturn {
	payNowDetails: IPayNow;
	messageComponent: ReactElement | null;
	messageSeverity: PayNowErrorSeverity | null;
	loading: boolean;
}

const usePayNowDetailsToken = (token: string): UsePayNowDetailsTokenReturn => {
	const dispatch = useAppDispatch();
	const payNowDetails = useAppSelector((state) => state.payNow);
	const [messageComponent, setMessageComponent] = useState<ReactElement | null>(null);
	const [messageSeverity, setMessageSeverity] = useState<PayNowErrorSeverity | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let isMounted = true;
		const abortController = new AbortController();

		const fetchPayNowDetails = async () => {
			if (!token) {
				handleMessage('noToken', PayNowErrorSeverity.ERROR);
				setLoading(false);
				return;
			}

			if (payNowDetails.status === 'failed') {
				handleMessage('fetchFailed', PayNowErrorSeverity.ERROR);
				setLoading(false);
				return;
			}
			try {
				const { payNowDetails, collectionOptions } = await dispatch(getPayNowDetailsAndPaymentOptions(token)).unwrap();

				validatePayNowDetails(payNowDetails, collectionOptions);
			} catch (error) {
				console.error('Error fetching pay now details:', error);
				if (isMounted) {
					handleMessage('fetchOptionsFailed', PayNowErrorSeverity.ERROR);
				}
			} finally {
				if (isMounted) {
					setLoading(false);
				}
			}
		};

		const validatePayNowDetails = (
			details: GetAgreementDetailsResponseModel,
			collectionOptions: GetPaymentCollectionOptionsResponseModel[],
		) => {
			if (!details || !collectionOptions) {
				handleMessage('fetchOptionsFailed', PayNowErrorSeverity.ERROR);
				return;
			}

			const { status, hasExpiredSplitPaymentPlan, policyIsCancelled, policyIsExpired, validUntil } = details;

			const currentDate = new Date();
			const validUntilDate = new Date(validUntil);
			if (status === PayNowStatus.ActivePaymentSchedule || hasExpiredSplitPaymentPlan) {
				handleMessage('scheduleExists', PayNowErrorSeverity.INFO);
			} else if (validUntilDate < currentDate) {
				handleMessage('linkExpired', PayNowErrorSeverity.WARNING);
			} else if (policyIsCancelled || policyIsExpired) {
				handleMessage('policyCancelled', PayNowErrorSeverity.WARNING);
			}
		};

		const handleMessage = (key: keyof typeof messages, severity: PayNowErrorSeverity) => {
			const message = messages[key];
			setMessageComponent(
				<Box sx={{ maxWidth: '600px' }}>
					<Typography variant='body1' color={severity} textAlign='center'>
						{message}
					</Typography>
				</Box>,
			);
			setMessageSeverity(severity);
		};

		if (payNowDetails.collectionOptions.length > 0 || payNowDetails.status === 'loading') {
			setLoading(false);
			return () => {
				isMounted = false;
			};
		}

		fetchPayNowDetails();

		return () => {
			isMounted = false;
			abortController.abort();
		};
	}, [token, payNowDetails.collectionOptions.length]);

	return { payNowDetails, messageComponent, messageSeverity, loading };
};

export default usePayNowDetailsToken;
